import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { money, money0 } from '../lib/format';
import { SiteFooter } from '../components/Layout';
import { PayNow, type GuestPayment } from '../components/PayNow';
import { Empty, Loading, Modal, Select, TextInput } from '../components/ui';

interface RoomTypeOffer {
  id: string;
  name: string;
  maxOccupancy: number;
  amenities: string[];
  imageUrl: string | null;
  tariff: number;
  gstRate: number;
  nights: number;
  subtotal: number;
  tax: number;
  total: number;
  available: number;
}

interface Availability {
  property: { name: string; city: string };
  nights: number;
  roomTypes: RoomTypeOffer[];
}

interface BookingResult {
  reference: string;
  roomType: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  total: number;
  status: string;
  payment: GuestPayment;
}

const day = (offset: number) =>
  new Date(Date.now() + offset * 86_400_000).toISOString().slice(0, 10);

/**
 * Booking a room from the hotel's own link.
 *
 * Deliberately the same shape as the QR menu: a guest who scanned a table code
 * to read the menu can book a room from the same address, with no account.
 */
export function BookRoom() {
  const { token = '' } = useParams();
  const [from, setFrom] = useState(day(1));
  const [to, setTo] = useState(day(2));
  const [chosen, setChosen] = useState<RoomTypeOffer | null>(null);
  const [booked, setBooked] = useState<BookingResult | null>(null);

  const offers = useQuery({
    queryKey: ['availability', token, from, to],
    queryFn: () => api.get<Availability>(`/api/public/menu/${token}/rooms?from=${from}&to=${to}`),
    retry: false,
    enabled: Boolean(token && from && to && to > from),
  });

  return (
    <div className="guest-shell">
      <header className="guest-head">
        <div className="brand-mark">K</div>
        <div>
          <h1>{offers.data?.property.name ?? 'Book a room'}</h1>
          <div className="small">{offers.data?.property.city ?? 'Choose your dates'}</div>
        </div>
      </header>

      <div className="guest-body">
        <div className="card">
          <div className="card-body">
            <div className="form-grid">
              <TextInput
                label="Check in"
                type="date"
                min={day(0)}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <TextInput
                label="Check out"
                type="date"
                min={from}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            {to <= from ? (
              <p className="small warn" style={{ marginTop: 8 }}>
                Check-out has to be after check-in.
              </p>
            ) : null}
          </div>
        </div>

        {offers.isLoading ? (
          <Loading label="Looking for rooms…" />
        ) : offers.error ? (
          <p className="small warn">
            {offers.error instanceof ApiError
              ? offers.error.message
              : 'Could not load rooms just now.'}
          </p>
        ) : (offers.data?.roomTypes.length ?? 0) === 0 ? (
          <Empty message="Nothing free for those dates — try moving them." />
        ) : (
          <div className="col gap-16">
            {offers.data!.roomTypes.map((type) => (
              <article key={type.id} className="card">
                <div className="card-body row between wrap gap-16">
                  <div className="row gap-16" style={{ minWidth: 0 }}>
                    {type.imageUrl ? (
                      <img className="room-photo" src={type.imageUrl} alt={type.name} />
                    ) : null}
                    <div style={{ minWidth: 0 }}>
                      <div className="b">{type.name}</div>
                      <div className="small muted">
                        Sleeps {type.maxOccupancy}
                        {type.amenities.length ? ` · ${type.amenities.join(', ')}` : ''}
                      </div>
                      <div className="xs muted" style={{ marginTop: 6 }}>
                        {money0(type.tariff)} a night · {type.nights} night
                        {type.nights === 1 ? '' : 's'} · GST {type.gstRate}%
                        {type.available <= 3 ? ` · only ${type.available} left` : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="guest-price">{money0(type.total)}</div>
                    <div className="xs muted">including tax</div>
                    <button
                      className="btn primary sm"
                      style={{ marginTop: 8 }}
                      onClick={() => setChosen(type)}
                    >
                      Book this
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <FindBooking token={token} property={offers.data?.property.name ?? 'the hotel'} />

        <p className="small muted center" style={{ marginTop: 16 }}>
          Looking for the food menu instead? <Link to={`/m/${token}`}>Open it here</Link>.
        </p>
      </div>

      {chosen ? (
        <BookingForm
          token={token}
          offer={chosen}
          from={from}
          to={to}
          onClose={() => setChosen(null)}
          onBooked={(result) => {
            setChosen(null);
            setBooked(result);
            void offers.refetch();
          }}
        />
      ) : null}

      {booked ? (
        <BookedModal
          property={offers.data?.property.name ?? 'the hotel'}
          booking={booked}
          onClose={() => setBooked(null)}
        />
      ) : null}

      <SiteFooter />
    </div>
  );
}

function BookingForm({
  token,
  offer,
  from,
  to,
  onClose,
  onBooked,
}: {
  token: string;
  offer: RoomTypeOffer;
  from: string;
  to: string;
  onClose: () => void;
  onBooked: (result: BookingResult) => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [adults, setAdults] = useState(1);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const book = useMutation({
    mutationFn: () =>
      api.post<BookingResult>(`/api/public/menu/${token}/bookings`, {
        roomTypeId: offer.id,
        from,
        to,
        guestName: name.trim(),
        guestPhone: phone.trim(),
        guestEmail: email.trim() || undefined,
        adults,
        note: note.trim() || undefined,
      }),
    onSuccess: onBooked,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not take that booking.'),
  });

  return (
    <Modal
      title={`Book a ${offer.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Back
          </button>
          <button
            className="btn primary"
            disabled={!name.trim() || !phone.trim() || book.isPending}
            onClick={() => {
              setError(null);
              book.mutate();
            }}
          >
            {book.isPending ? 'Booking…' : `Book · ${money0(offer.total)}`}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        {error ? <div className="form-error">{error}</div> : null}
        <div className="col gap-6">
          <div className="row between">
            <span>
              {offer.nights} night{offer.nights === 1 ? '' : 's'} at {money0(offer.tariff)}
            </span>
            <span>{money(offer.subtotal)}</span>
          </div>
          <div className="row between">
            <span className="muted">GST {offer.gstRate}%</span>
            <span>{money(offer.tax)}</span>
          </div>
          <div
            className="row between"
            style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}
          >
            <b>Total</b>
            <b>{money(offer.total)}</b>
          </div>
        </div>

        <div className="form-grid">
          <TextInput
            className="span2"
            label="Your name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextInput
            label="Phone *"
            placeholder="+91 98450 12345"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <TextInput
            label="Email"
            type="email"
            hint="For your confirmation"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Select
            label="Guests"
            value={String(adults)}
            onChange={(e) => setAdults(Number(e.target.value))}
          >
            {Array.from({ length: offer.maxOccupancy }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n} {n === 1 ? 'guest' : 'guests'}
              </option>
            ))}
          </Select>
          <TextInput
            label="Anything we should know?"
            placeholder="Late arrival, ground floor"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * What happens after the room is held: pay now, or pay at the desk.
 *
 * The booking exists either way. Paying is what turns a hold into a confirmed
 * booking, and the hotel is told so by the gateway's signed callback, never by
 * this page saying it went through.
 */
function BookedModal({
  property,
  booking,
  onClose,
}: {
  property: string;
  booking: BookingResult;
  onClose: () => void;
}) {
  const [paid, setPaid] = useState(false);

  return (
    <Modal title={paid ? 'Booking confirmed' : 'Room held'} onClose={onClose}>
      <div className="col gap-16" style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>&#10003;</div>
        <div>
          <b>
            {booking.roomType} is {paid ? 'booked' : 'held'} for you.
          </b>
          <div className="small muted" style={{ marginTop: 6 }}>
            Reference <b className="mono">{booking.reference}</b>
          </div>
        </div>
        <div className="small">
          {booking.checkIn} to {booking.checkOut} · {booking.nights} night
          {booking.nights === 1 ? '' : 's'} · <b>{money(booking.total)}</b>
        </div>

        <PayNow
          payment={booking.payment}
          name={property}
          description={`${booking.roomType} · ${booking.checkIn} to ${booking.checkOut}`}
          fallback="Or pay at the hotel when you arrive — the room is held either way."
          onPaid={() => setPaid(true)}
        />
      </div>
    </Modal>
  );
}

interface FoundBooking {
  reference: string;
  guestName: string;
  roomType: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  total: number;
  status: string;
  paid: boolean;
}

/**
 * Finding a booking again, and paying for it.
 *
 * Everything else about a booking is reachable only in the moment it is made.
 * A guest who closed the tab has their reference and nothing to do with it -
 * so this takes the reference, shows them where they stand, and opens a fresh
 * payment if there is still money owed.
 */
function FindBooking({ token, property }: { token: string; property: string }) {
  const [reference, setReference] = useState('');
  const [found, setFound] = useState<FoundBooking | null>(null);
  const [payment, setPayment] = useState<GuestPayment | null>(null);
  const [error, setError] = useState<string | null>(null);

  const look = useMutation({
    mutationFn: () =>
      api.get<FoundBooking>(
        `/api/public/menu/${token}/bookings/${encodeURIComponent(reference.trim().toUpperCase())}`,
      ),
    onSuccess: (booking) => {
      setFound(booking);
      setPayment(null);
      setError(null);
    },
    onError: (caught) => {
      setFound(null);
      setError(
        caught instanceof ApiError ? caught.message : 'Could not find that booking just now.',
      );
    },
  });

  const pay = useMutation({
    mutationFn: () =>
      api.post<GuestPayment>(
        `/api/public/menu/${token}/bookings/${encodeURIComponent(reference.trim().toUpperCase())}/pay`,
      ),
    onSuccess: setPayment,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not start that payment.'),
  });

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div className="card-head">
        <h2>Already booked?</h2>
        <span className="small muted">Find it with your reference</span>
      </div>
      <div className="card-body col gap-16">
        <div className="row gap-6 wrap" style={{ alignItems: 'flex-end' }}>
          <TextInput
            label="Booking reference"
            placeholder="BK-XXXXXXXXXX"
            value={reference}
            onChange={(e) => setReference(e.target.value.toUpperCase())}
          />
          <button
            className="btn"
            disabled={!reference.trim() || look.isPending}
            onClick={() => look.mutate()}
          >
            {look.isPending ? 'Looking…' : 'Find it'}
          </button>
        </div>

        {error ? <p className="small warn">{error}</p> : null}

        {found ? (
          <div className="col gap-6">
            <div className="b">
              {found.roomType} · {found.checkIn} to {found.checkOut}
            </div>
            <div className="small muted">
              {found.guestName} · {found.nights} night{found.nights === 1 ? '' : 's'} ·{' '}
              {money(found.total)}
            </div>

            {found.paid ? (
              <p className="small">Paid in full. Show this reference at the desk.</p>
            ) : found.status === 'CANCELLED' ? (
              <p className="small warn">This booking was cancelled.</p>
            ) : found.status === 'CHECKED_IN' ? (
              // Offering to pay would only earn a refusal: the folio is the
              // desk's now, and it settles at check-out.
              <p className="small">You have checked in — this settles at the desk.</p>
            ) : payment ? (
              <PayNow
                payment={payment}
                name={property}
                description={`Booking ${found.reference}`}
                fallback="Or pay at the hotel when you arrive."
              />
            ) : (
              <button className="btn primary" disabled={pay.isPending} onClick={() => pay.mutate()}>
                {pay.isPending ? 'Opening…' : `Pay ${money(found.total)} now`}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
